import { useEffect, useState } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import {
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
} from "../../../components/icons";
import { ifasColors } from "../ifas/components/OperationsUI";
import { STANDARD_TONES, standardGhostBtnSx } from "../ifas/components/StandardUI";

/**
 * Restrained, fixed persistent strip — `.gstrip` in `fleet-portal-shell.html`:
 * alerts count pill, vehicle/driver availability, "Updated Xs ago", and a
 * shared Refresh (the SAME 30s poller Fleet already runs — no second one).
 *
 * Presentational only. Takes everything it needs from the caller's existing
 * `fleetSummary` state; it does not fetch or poll anything itself.
 */
export default function FleetGlobalStrip({
  alertCount,
  availableVehicles,
  availableDrivers,
  generatedAt,
  isRefreshing = false,
  onRefresh,
  onAlertsClick,
}: {
  alertCount: number;
  availableVehicles: number;
  availableDrivers: number;
  generatedAt?: string | null;
  isRefreshing?: boolean;
  onRefresh: () => void;
  onAlertsClick?: () => void;
}) {
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const updatedSecondsAgo = (() => {
    if (!generatedAt) return null;
    const generatedMs = new Date(generatedAt).getTime();
    if (Number.isNaN(generatedMs)) return null;
    return Math.max(0, Math.round((nowTick - generatedMs) / 1000));
  })();

  const amber = STANDARD_TONES.amber;
  const green = STANDARD_TONES.green;

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.75,
        flexWrap: "wrap",
        border: `1px solid ${ifasColors.border}`,
        borderRadius: "12px",
        bgcolor: ifasColors.surface,
        boxShadow: "0 4px 14px rgba(20,24,31,.05)",
        px: 1.75,
        py: 1.25,
        mb: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
        <Box
          component={onAlertsClick ? "button" : "div"}
          type={onAlertsClick ? "button" : undefined}
          onClick={onAlertsClick}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.875,
            fontSize: "12.5px",
            fontWeight: 800,
            color: amber.color,
            bgcolor: amber.bg,
            borderRadius: "999px",
            px: 1.375,
            py: 0.75,
            // Responsive pass (F.9): this pill navigates to the Alerts tab, so
            // it is a real control and needs a real touch target. Desktop (lg+)
            // keeps the mock's compact pill height exactly.
            minHeight: onAlertsClick ? { xs: 44, lg: "auto" } : undefined,
            border: "none",
            fontFamily: "inherit",
            cursor: onAlertsClick ? "pointer" : "default",
            "& .MuiSvgIcon-root": { fontSize: 15 },
          }}
        >
          <WarningAmberOutlinedIcon />
          {alertCount} alert{alertCount === 1 ? "" : "s"}
        </Box>
        <Box sx={{ width: "1px", height: 20, bgcolor: ifasColors.border }} />
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: ifasColors.muted }}>
            <Box
              component="span"
              sx={{ width: 7, height: 7, borderRadius: "999px", bgcolor: green.color, display: "inline-block", mr: "5px", verticalAlign: "middle" }}
            />
            <Box component="b" sx={{ color: ifasColors.text, fontWeight: 900 }}>{availableVehicles}</Box>{" "}
            vehicles free
          </Typography>
          <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: ifasColors.muted }}>
            <Box
              component="span"
              sx={{ width: 7, height: 7, borderRadius: "999px", bgcolor: green.color, display: "inline-block", mr: "5px", verticalAlign: "middle" }}
            />
            <Box component="b" sx={{ color: ifasColors.text, fontWeight: 900 }}>{availableDrivers}</Box>{" "}
            drivers free
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            bgcolor: ifasColors.surface,
            border: `1px solid ${ifasColors.border}`,
            borderRadius: "999px",
            px: 1.5,
            py: 0.875,
            fontSize: "12px",
            fontWeight: 700,
            color: ifasColors.muted,
          }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: "999px", bgcolor: green.color }} />
          {updatedSecondsAgo === null
            ? isRefreshing
              ? "Updating…"
              : "Not yet updated"
            : `Updated ${updatedSecondsAgo}s ago`}
        </Box>
        <Button
          size="small"
          startIcon={<RefreshOutlinedIcon />}
          onClick={onRefresh}
          disabled={isRefreshing}
          sx={standardGhostBtnSx}
        >
          Refresh
        </Button>
      </Stack>
    </Paper>
  );
}
